import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, RotateCw, Copy, CheckCircle2, Send, XCircle } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import {
  getMe,
  updateWebhook,
  rotateWebhookSecret,
  sendTestWebhook,
  type WebhookTestResult
} from '@/api/account';

export function WebhookScreen() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: getMe
  });

  const [url, setUrl] = useState<string | null>(null);
  const [rawSecret, setRawSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [testResult, setTestResult] = useState<WebhookTestResult | null>(null);

  const effectiveUrl = url ?? data?.webhookUrl ?? '';

  const updateMut = useMutation({
    mutationFn: (next: string) => updateWebhook(next),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] });
      toast.success('Webhook URL saved');
    },
    onError: (e: Error) => toast.error(e.message || 'Save failed')
  });

  const rotateMut = useMutation({
    mutationFn: () => rotateWebhookSecret(),
    onSuccess: r => {
      setRawSecret(r.webhookSecret);
      toast.success('Secret rotated — copy the new value now');
    },
    onError: (e: Error) => toast.error(e.message || 'Rotation failed')
  });

  const testMut = useMutation({
    mutationFn: () => sendTestWebhook(),
    onSuccess: r => {
      setTestResult(r);
      if (r.ok) {
        toast.success(`Test delivered — ${r.status} in ${r.latencyMs}ms`);
      } else {
        toast.error(
          `Test failed — ${r.error || `HTTP ${r.status}`} in ${r.latencyMs}ms`
        );
      }
    },
    onError: (e: Error) => toast.error(e.message || 'Test request failed')
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    updateMut.mutate(effectiveUrl.trim());
  };

  const copy = async (v: string) => {
    await navigator.clipboard.writeText(v);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  if (isLoading || !data) {
    return (
      <div className="flex items-center gap-3 text-nurev-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading webhook config…
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <Toaster theme="dark" position="bottom-right" richColors />

      <header>
        <h1 className="text-2xl font-bold text-nurev-text">Webhook</h1>
        <p className="mt-1 text-sm text-nurev-muted">
          NUREV calls your URL when a batch embedding job completes. Each
          payload is signed with your webhook secret as{' '}
          <code className="code-pill">X-NUREV-Signature: sha256=…</code>.
          Recompute <code className="code-pill">hmac(secret, raw_body)</code>{' '}
          on your end and compare.
        </p>
      </header>

      <section className="card">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-nurev-purple-light">
          Endpoint URL
        </h2>
        <form onSubmit={onSubmit} className="space-y-4">
          <input
            type="url"
            placeholder="https://your-server.example.com/nurev-webhooks"
            className="input"
            value={effectiveUrl}
            onChange={e => setUrl(e.target.value)}
          />
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={updateMut.isPending}
              className="btn-primary"
            >
              {updateMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Save URL'
              )}
            </button>
            {url !== null && (
              <button
                type="button"
                onClick={() => setUrl(null)}
                className="btn-ghost"
              >
                Reset
              </button>
            )}
          </div>
          <p className="text-xs text-nurev-muted">
            Must use <code>https://</code>. Leave blank to disable webhooks.
          </p>
        </form>
      </section>

      <section className="card">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-nurev-purple-light">
          Signing secret
        </h2>
        <p className="mb-4 text-sm text-nurev-text-soft">
          The current secret is stored hashed and can never be shown again.
          Rotating issues a new one — the previous secret stops signing
          immediately, so update your verification code in the same window
          you do this.
        </p>
        <button
          onClick={() => rotateMut.mutate()}
          disabled={rotateMut.isPending}
          className="btn-secondary"
        >
          {rotateMut.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <RotateCw className="h-4 w-4" /> Rotate secret
            </>
          )}
        </button>

        {rawSecret && (
          <div className="mt-5 rounded-xl border border-amber-500/50 bg-amber-500/5 p-4">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-amber-300">
              New secret — copy now, this won't be shown again
            </p>
            <div className="flex items-center gap-3">
              <code className="flex-1 select-all rounded-lg border border-nurev-border bg-nurev-surface px-3 py-2 font-mono text-xs text-nurev-text">
                {rawSecret}
              </code>
              <button onClick={() => copy(rawSecret)} className="btn-secondary">
                {copied ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 text-green-400" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" /> Copy
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="card">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-nurev-purple-light">
          Test delivery
        </h2>
        <p className="mb-4 text-sm text-nurev-text-soft">
          Fires a signed{' '}
          <code className="code-pill">webhook.test</code> payload at the URL
          configured above. Use it to verify your endpoint receives the
          request and that your HMAC verification accepts the signature
          before relying on it for real batch completions.
        </p>
        <button
          onClick={() => testMut.mutate()}
          disabled={testMut.isPending || !data.webhookUrl}
          className="btn-secondary"
          title={
            !data.webhookUrl
              ? 'Save a webhook URL first'
              : 'Send a signed test payload'
          }
        >
          {testMut.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <Send className="h-4 w-4" /> Send test
            </>
          )}
        </button>

        {testResult && (
          <div
            className={`mt-5 rounded-xl border p-4 ${
              testResult.ok
                ? 'border-emerald-500/50 bg-emerald-500/5'
                : 'border-red-500/50 bg-red-500/5'
            }`}
          >
            <div className="mb-3 flex items-center gap-2">
              {testResult.ok ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
              ) : (
                <XCircle className="h-5 w-5 text-red-400" />
              )}
              <span
                className={`text-sm font-semibold ${
                  testResult.ok ? 'text-emerald-300' : 'text-red-300'
                }`}
              >
                {testResult.ok
                  ? `HTTP ${testResult.status} in ${testResult.latencyMs}ms`
                  : testResult.error
                  ? `${testResult.error} (${testResult.latencyMs}ms)`
                  : `HTTP ${testResult.status} in ${testResult.latencyMs}ms`}
              </span>
            </div>
            <p className="mb-3 text-xs text-nurev-text-soft">
              {testResult.hint}
            </p>
            <div className="grid grid-cols-1 gap-3 text-xs">
              <div>
                <p className="mb-1 font-semibold uppercase tracking-wide text-nurev-muted">
                  Signature header sent
                </p>
                <code className="block select-all rounded-lg border border-nurev-border bg-nurev-surface px-3 py-2 font-mono text-nurev-text">
                  X-NUREV-Signature: {testResult.signatureSent}
                </code>
              </div>
              {testResult.responseSample && (
                <div>
                  <p className="mb-1 font-semibold uppercase tracking-wide text-nurev-muted">
                    Your endpoint's response (first 500 chars)
                  </p>
                  <pre className="overflow-x-auto rounded-lg border border-nurev-border bg-nurev-surface px-3 py-2 font-mono text-nurev-text-soft">
                    {testResult.responseSample}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
